import { Alert, Box, Button, Drawer, Grid, Group, Modal, SimpleGrid, Stack, Text } from "@mantine/core"
import { showNotification } from "@dehub/theme"
import classes from "./detail-form.module.css"
import { CaretLeftIcon, CheckIcon, FileTextIcon, InfoIcon, XIcon } from "@phosphor-icons/react";
import * as InvoiceApi from "../../apis/invoice"
import { OrderApi } from "../../apis"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { useNavigate } from "react-router"
import { IGeneral } from "../../store/General"
import { dateTimeSecFormat } from "../../utils/date"
import HttpHandler from "../../utils/http-handler"
import { message } from "../../utils/message"
import { renderBusinessName } from "../../utils/render-business-name"
import { renderName } from "../../utils/render-name"
import { PageLayout } from "../layout"
import { buildOrderStatusSteps } from "../../utils/order-status-steps"
import { DeliveryLocationMap, isDeliveryLocationEditable } from "../order/delivery-location-map"
import { DetailCard, DetailField } from "../order/detail-card"
import { OrderStepper } from "../order/order-stepper"
import { useSalesActions } from "../order/order-actions"
import { usePermission } from "../permission"
import { ReasonTooltip } from "../ui"
import { DotPill } from "../delivery/atoms"
import { orderStatusTone } from "../buyer-sales/order-status-pill"
import { MultiDeliverySummaryWidget } from "./MultiDeliverySummaryWidget"
import { DeliveryDateChangePanel } from "./DeliveryDateChangePanel"
import { OrderConsignmentContextPanel } from "./OrderConsignmentContextPanel"
import { SupplierSalesDynamicConfirmForm } from "./confirm-form"
import { SupplierSalesLinesCard, SupplierSalesSummaryColumn } from "./dynamic-detail-form"
import { SupplierSalesDynamicReviewForm } from "./review-form"
import { SupplierSalesShippingForm } from "./shipping-form"
import { CancelConfirmedOrderButton } from "../order-cancel/CancelConfirmedOrderButton"
import { CancelOrderModal } from "../order-cancel/CancelOrderModal"
import { SupplierSalesSplitForm } from "./split-form"

export function SupplierSalesDetailForm({ action, reload }: { action: any; reload?: any }) {
  const breadcrumbs = useBreadcrumb()
  const { t } = useTranslation(["supplierSales", "common"])
  const navigate = useNavigate()
  const [actions, setActions] = useState<string[]>([])
  const [checked, setChecked] = useState<string[]>([])
  const [isDropshipping, setIsDropshipping] = useState<string[]>([])
  const [invoicePdfBusy, setInvoicePdfBusy] = useState(false)
  const [cancelBusy, setCancelBusy] = useState(false)
  const ORD_SAVE = usePermission("ORD_SAVE")
  const ORD_SEND = usePermission("ORD_SEND")
  const { stateOf, permissionState } = useSalesActions()

  const { orderStatus, deliveryDateTypes } = useSelector((state: { general: IGeneral }) => state.general)

  const order = action?.[1]
  const { steps: statusSteps, currentStatus: effectiveStatus } = buildOrderStatusSteps(order, orderStatus, { perspective: "sent" })
  const statusObj = orderStatus.find((c: any) => c.code === effectiveStatus)
  const canEditGps = isDeliveryLocationEditable(order?.orderStatus) && (ORD_SAVE.hasAccess || ORD_SEND.hasAccess)

  // Хянах stays visible; the reason is the permission or, when the status is wrong, the status.
  const reviewState = stateOf("review", order)
  const reviewStatusReason = reviewState.applies ? null : t("supplierSales:sales.reviewNotNeeded", { status: statusObj?.sentName || "-" })
  const reviewReason = permissionState("review").reason ?? reviewStatusReason
  const cancelState = stateOf("cancel", order)
  const cancelReason = permissionState("cancel").reason ?? (cancelState.applies ? null : t("supplierSales:sales.cancelNotAvailable", { status: statusObj?.sentName || "-" }))

  const partnerLine = order?.receiverBusiness?.partner?.refCode || order?.receiverBusiness?.partner?.businessName
    ? [order?.receiverBusiness?.partner?.refCode, renderBusinessName(order?.receiverBusiness?.partner, "")].filter(Boolean).join(" ")
    : "-"

  const onCancelOrder = async () => {
    setCancelBusy(true)
    try {
      await OrderApi.cancel(order?.id)
      message.success(t("supplierSales:sales.cancelSuccess"))
      setActions([])
      await reload?.()
    } catch (err) {
      message.error((err as HttpHandler)?.message!)
    } finally {
      setCancelBusy(false)
    }
  }

  const onSubmit = (value: any) => {
    if ((value || []).some((item: any) => !item.isDropshipping)) {
      setIsDropshipping(["unSuccessDropping", value as any])
    } else {
      setIsDropshipping(["successDropping", value as any])
    }
  }

  const onCancel = async () => {
    setIsDropshipping([])
    setChecked([])
  }

  const handleDownloadInvoice = async () => {
    if (!order?.invoiceId) return
    setInvoicePdfBusy(true)
    try {
      const url = await InvoiceApi.getPdf(order.invoiceId)
      if (url) window.open(url, "_blank", "noopener,noreferrer")
      else throw new Error(t("supplierSales:sales.invoicePdfEmpty", { defaultValue: "Нэхэмжлэхийн PDF хоосон байна" }))
    } catch (e) {
      showNotification({ color: "red", title: t("common:messages.error", { defaultValue: "Алдаа" }), message: (e as Error).message })
    } finally {
      setInvoicePdfBusy(false)
    }
  }

  const onSplit = (value: any) => {
    if ((checked || []).some((item: any) => item.isSplit || item.isDropshipping || item.isDropped || item.isLoadable)) {
      setIsDropshipping(["unSuccessSplit", value as any])
    } else {
      setIsDropshipping(["successSplit", value as any])
    }
  }

  return (
    <>
      <PageLayout
        title={
          <Group gap="sm" wrap="nowrap">
            {order?.salesCode ? <Text span inherit ff="monospace">{order.salesCode}</Text> : <Text span inherit>{t("supplierSales:sales.orderDetail")}</Text>}
            {/* Dot pill as in the canvas; the catalogue colour (light grey for a draft) is unreadable as text. */}
            {statusObj && <DotPill tone={orderStatusTone(effectiveStatus)}>{statusObj.sentName || statusObj.name}</DotPill>}
          </Group>
        }
        subTitle={t("supplierSales:sales.detailSubtitle", {
          date: dateTimeSecFormat(order?.createdAt),
          name: renderName(order?.orderedUser) || "-",
        })}
        breadcrumb={breadcrumbs}
        extra={[
          <Button onClick={() => navigate("/supplier-sales")} variant="default" key="back" leftSection={<CaretLeftIcon size={16} />}>
            {t("common:actions.back")}
          </Button>,
          ...(order?.invoiceId
            ? [(
              <Button
                key="invoice-pdf"
                variant="default"
                loading={invoicePdfBusy}
                leftSection={<FileTextIcon size={16} />}
                onClick={handleDownloadInvoice}
              >
                {t("supplierSales:sales.downloadInvoice")}
              </Button>
            )]
            : []),
          <ReasonTooltip key="review" reason={reviewReason}>
            <Button variant="default" leftSection={<CheckIcon size={16} />} onClick={() => setActions(["review"])}>
              {t("supplierSales:sales.review")}
            </Button>
          </ReasonTooltip>,
          ...(stateOf("approve", order).applies
            ? [
              <ReasonTooltip key="approve" reason={permissionState("approve").reason}>
                <Button onClick={() => setActions(["confirm"])}>{t("supplierSales:sales.approve")}</Button>
              </ReasonTooltip>,
              <ReasonTooltip key="decline" reason={permissionState("decline").reason}>
                <Button variant="default" onClick={() => setActions(["reject"])}>{t("supplierSales:sales.decline")}</Button>
              </ReasonTooltip>,
            ]
            : []),
          ...(stateOf("cancelConfirmed", order).applies
            ? [<CancelConfirmedOrderButton key="cancel" order={order as any} onSuccess={reload} />]
            : [
              <ReasonTooltip key="cancel" reason={cancelReason}>
                <Button variant="default" leftSection={<XIcon size={16} />} onClick={() => setActions(["cancel"])}>
                  {t("supplierSales:sales.cancel")}
                </Button>
              </ReasonTooltip>,
            ]),
        ]}>

        {/* Order progress: plain white card, dark done, ringed current, grey pending (Ord_SalesDetail). */}
        {statusSteps.length > 0 && (
          <Box className={classes.stepsCard}>
            <OrderStepper steps={statusSteps} currentStatus={effectiveStatus} checkDone />
          </Box>
        )}

        {/* Consignment context (renders only for CONSIGNMENT_* orders) */}
        <OrderConsignmentContextPanel order={order as any} onAfterRevert={reload} />
        <MultiDeliverySummaryWidget order={order as any} />
        {/* Buyer-requested delivery date changes; renders nothing when none were asked. */}
        <DeliveryDateChangePanel orderId={order?.id} reload={reload} />

        <Grid gap="md" align="flex-start">
          {/* Left (takes the rest): customer, receiving + location, order lines */}
          <Grid.Col span={{ base: 12, lg: "auto" }} miw={0}>
            <Stack gap="md">
              <DetailCard title={t("supplierSales:sales.customerInfo")}>
                <SimpleGrid cols={{ base: 1, sm: 2, md: 3 }} spacing="lg" verticalSpacing="sm">
                  <DetailField label={t("supplierSales:sales.partnerName")}>{partnerLine}</DetailField>
                  <DetailField label={t("supplierSales:sales.supplierName")}>
                    {order?.receiverBusiness?.refCode || order?.receiverBusiness?.profileName
                      ? [order?.receiverBusiness?.refCode, renderBusinessName(order?.receiverBusiness, "")].filter(Boolean).join(" ")
                      : "-"}
                  </DetailField>
                  <DetailField label={t("supplierSales:sales.dTaxId")} mono>{order?.receiverBusiness?.regNumber || "-"}</DetailField>
                  <DetailField label={t("supplierSales:sales.paymentTerm")}>{order?.paymentTerm?.description || "-"}</DetailField>
                  <DetailField label={t("supplierSales:sales.supplierManager")}>{renderName(order?.supplierStaff) || "-"}</DetailField>
                  <DetailField label={t("supplierSales:sales.buyerManager")}>{renderName(order?.buyerStaff) || "-"}</DetailField>
                </SimpleGrid>
              </DetailCard>

              <Grid gap="md" align="stretch">
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <DetailCard title={t("supplierSales:sales.dReceive")} fill>
                    <Stack gap="sm">
                      <DetailField label={t("supplierSales:sales.dStaff")}>{renderName(order?.receiverStaff) || "-"}</DetailField>
                      <DetailField label={t("supplierSales:sales.dBranch")}>{order?.receiverBranch?.name || "-"}</DetailField>
                      <DetailField label={t("supplierSales:sales.dAddress")}>
                        {order?.receiverBranch?.branchAddress || "-"}
                      </DetailField>
                      {/* One field as in the canvas: «Хуваарьт өдөр, хамгийн ойрхон» over the date. */}
                      <DetailField
                        label={t("supplierSales:sales.dNearest", {
                          type: deliveryDateTypes.find((c: any) => c?.code === order?.deliveryType)?.name || t("supplierSales:sales.deliveryType"),
                        })}
                        mono>
                        {order?.deliveryDate ? dateTimeSecFormat(order?.deliveryDate) : "-"}
                      </DetailField>
                    </Stack>
                  </DetailCard>
                </Grid.Col>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <DeliveryLocationMap
                    orderId={order?.id}
                    latitude={order?.deliveryLatitude}
                    longitude={order?.deliveryLongitude}
                    fallbackLat={order?.receiverBranch?.locationLat}
                    fallbackLng={order?.receiverBranch?.locationLng}
                    canEdit={canEditGps}
                    onSaved={reload}
                    height={240}
                    quiet
                    unsetInBody
                  />
                </Grid.Col>
              </Grid>

              <SupplierSalesLinesCard order={order} checked={checked} setChecked={setChecked} onSubmit={onSubmit} onSplit={onSplit} />
            </Stack>
          </Grid.Col>

          {/* Right, 340 wide as in Ord_SalesDetail: payment amount, summary, additional, attachments, payment confirmation */}
          <Grid.Col span={{ base: 12, lg: "content" }}>
            <Box w={{ base: "100%", lg: 340 }}>
              <SupplierSalesSummaryColumn order={order} />
            </Box>
          </Grid.Col>
        </Grid>
      </PageLayout>

      <CancelOrderModal
        opened={actions[0] === "cancel"}
        code={String(order?.salesCode ?? "-")}
        loading={cancelBusy}
        onClose={() => setActions([])}
        onConfirm={onCancelOrder}
      />
      <Modal
        opened={actions[0] === "review"}
        onClose={() => setActions([])}
        withCloseButton={false}
        size="600px"
        padding={0}
        centered>
        <SupplierSalesDynamicReviewForm action={order} setActions={setActions} reviewed={"reviewed"} />
      </Modal>
      <Modal
        opened={actions[0] === "confirm" || actions[0] === "reject"}
        onClose={() => setActions([])}
        withCloseButton={false}
        size="600px"
        padding={0}
        centered>
        <SupplierSalesDynamicConfirmForm
          action={order}
          setActions={setActions}
          constant={actions[0] === "reject" ? "reject" : "confirm"}
          reload={reload}
        />
      </Modal>
      <Modal opened={isDropshipping[0] === "unSuccessDropping"} onClose={() => setIsDropshipping([])} withCloseButton={false} size="lg" padding={0} centered>
        <Alert variant="light" color="yellow" title={t("supplierSales:sales.notice")} icon={<InfoIcon />}>
          <Text
            size="sm"
            fw={500}
            c="var(--mantine-color-dark-5)"
            style={{ textAlign: "justify", letterSpacing: "1px", textIndent: "20px" }}>
            {t("supplierSales:sales.selectedProducts")}{" "}
            {(checked || []).some((item: any) => !item.isDropshipping) && (
              <>
                SKU:{" "}
                {(checked || [])
                  .filter((item: any) => !item.isDropshipping)
                  .map((item: any, index: number) => (
                    <Text span key={index}>{item?.skuCode} </Text>
                  ))}
                {t("supplierSales:sales.supplierCannotDeliver")}{" "}
              </>
            )}
            {(checked || []).some((item: any) => item.isDropped) && (
              <>
                SKU:{" "}
                {(checked || [])
                  .filter((item: any) => item.isDropped)
                  .map((item: any, index: number) => (
                    <Text span key={index}>{item?.skuCode} </Text>
                  ))}
                {t("supplierSales:sales.deliveryTransferredToSupplier")}
              </>
            )}
            {(checked || []).some((item: any) => item.isConfirmed) && (
              <>
                SKU:{" "}
                {(checked || [])
                  .filter((item: any) => item.isConfirmed)
                  .map((item: any, index: number) => (
                    <Text span key={index}>{item?.skuCode} </Text>
                  ))}
                {t("supplierSales:sales.productSentToDistribution")}
              </>
            )}
            {(checked || []).some((item: any) => item.isSplit) && (
              <>
                SKU:{" "}
                {(checked || [])
                  .filter((item: any) => item.isSplit)
                  .map((item: any, index: number) => (
                    <Text span key={index}>{item?.skuCode} </Text>
                  ))}
                {t("supplierSales:sales.productSplitFromOrder")}
              </>
            )}
          </Text>
        </Alert>
      </Modal>
      <Drawer
        opened={isDropshipping[0] === "successDropping"}
        onClose={() => setIsDropshipping([])}
        position="right"
        size={720}
        title={t("supplierSales:dropshipping.createTitle")}>
        <SupplierSalesShippingForm
          action={order?.id}
          isDropshipping={isDropshipping}
          onCancel={onCancel}
          onSuccess={async () => {
            await reload()
            onCancel && onCancel()
          }}
        />
      </Drawer>
      <Modal
        opened={isDropshipping[0] === "unSuccessSplit"}
        onClose={() => setIsDropshipping([])}
        withCloseButton={false}
        size="lg"
        padding={0}
        centered>
        <Alert variant="light" color="yellow" title={t("supplierSales:sales.notice")} icon={<InfoIcon />}>
          <Text
            size="sm"
            fw={500}
            c="var(--mantine-color-dark-5)"
            style={{ textAlign: "justify", letterSpacing: "1px", textIndent: "20px" }}>
            {t("supplierSales:sales.selectedProducts")}{" "}
            {(checked || []).some((item: any) => item.isDropshipping) && (
              <>
                SKU:{" "}
                {(checked || [])
                  .filter((item: any) => item.isDropshipping)
                  .map((item: any, index: number) => (
                    <Text span key={index}>{item?.skuCode} </Text>
                  ))}
                {t("supplierSales:sales.cannotSplitOrder")}{" "}
              </>
            )}
            {(checked || []).some((item: any) => item.isDropped) && (
              <>
                SKU:{" "}
                {(checked || [])
                  .filter((item: any) => item.isDropped)
                  .map((item: any, index: number) => (
                    <Text span key={index}>{item?.skuCode} </Text>
                  ))}
                {t("supplierSales:sales.deliveryTransferredToSupplier")}
              </>
            )}
            {(checked || []).some((item: any) => item.isSplit) && (
              <>
                SKU:{" "}
                {(checked || [])
                  .filter((item: any) => item.isSplit)
                  .map((item: any, index: number) => (
                    <Text span key={index}>{item?.skuCode} </Text>
                  ))}
                {t("supplierSales:sales.productSplitFromOrder")}
              </>
            )}
            {(checked || []).some((item: any) => item.isLoadable) && (
              <>
                SKU:{" "}
                {(checked || [])
                  .filter((item: any) => item.isLoadable)
                  .map((item: any, index: number) => (
                    <Text span key={index}>{item?.skuCode} </Text>
                  ))}
                {t("supplierSales:sales.loadingPermissionGranted")}
              </>
            )}
          </Text>
        </Alert>
      </Modal>
      <Modal
        opened={isDropshipping[0] === "successSplit"}
        onClose={() => setIsDropshipping([])}
        withCloseButton={false}
        size="60%"
        padding={0}
        centered>
        <SupplierSalesSplitForm
          id={order?.id}
          isDropshipping={isDropshipping}
          onCancel={onCancel}
          onSuccess={async () => {
            await reload()
            onCancel && onCancel()
          }}
        />
      </Modal>
    </>
  )
}

const useBreadcrumb = () => {
  const { t } = useTranslation(["supplierSales", "common"])
  return [
    { to: "/", label: t("common:breadcrumb.home") },
    { to: "/supplier-sales", label: t("supplierSales:sales.orderList") },
    { label: t("supplierSales:sales.orderDetail") },
  ]
}

